import { useRef, useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import useTranslation from "../hooks/useTranslation";
import { useGeneratedTextCreate } from "../hooks/useGeneratedTextCreate";
import {
  useGeneratedTextCreateFromImage,
  useImageReadingLayoutAnalysis,
  type ImageReadingTextWorkflow,
  type ImageReadingLayoutAnalysis,
  type ImageReadingSelectionBlock,
  type ImageReadingArticleCandidateBlock,
  type ImageReadingArticleCandidateDiagnostic,
} from "../hooks/useGeneratedTextCreateFromImage";
import { useGeneratedTextCreateFromArticleUrl } from "../hooks/useGeneratedTextCreateFromArticleUrl";
import { SubmitHandler, useForm } from "react-hook-form";
import {
  ArrowRightIcon,
  CameraIcon,
  AdjustmentsHorizontalIcon,
  XMarkIcon,
  DocumentTextIcon,
  SparklesIcon,
  PhotoIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import {
  getLevelOptionsForLanguage,
  getLevelTranslationKey,
  isLevelAllowedForLanguage,
  TEXT_LANGUAGES,
} from "../constants";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { notifySnackbar } from "../lib/notifySnackbar";
import MySelect from "./MySelect";
import { useUserSettings } from "../hooks/useUserSettings";
import { useLanguageSpace } from "../hooks/useLanguageSpace";
import { LOGIN_STATUS_QUERY_KEY } from "../hooks/useLoginStatus";
import { getDefaultTextType } from "../mvpCapabilities";
import {
  useTopicIdeaNext,
  type TopicIdeaDto,
} from "../hooks/useTopicIdeaNext";
import {
  usePhotoVocabularyImportCreate,
  usePhotoVocabularyImport,
  usePhotoVocabularyImportFinalize,
  usePhotoVocabularyImportPreviewTranslations,
  type PhotoVocabularyImportMode,
  type PhotoVocabularyImportWord,
} from "../hooks/usePhotoVocabularyImport";
import { invalidateQuery as invalidateVocabularyListQuery } from "../hooks/usePaginatedVocabularyList";
import { prepareImageUploadFile } from "../utils/prepareImageUploadFile";

type FormDefaults = {
  typ?: string;
  language?: string;
  level?: string;
  tense?: string;
  word_number?: string;
  main_character?: string;
};

type FormDefaultsByLanguage = Record<string, FormDefaults>;

const DEFAULT_TEXT_TYPE = getDefaultTextType();
const DEFAULT_LEVEL = "A2";
const DEFAULT_TENSE = "A";
const VALID_TENSES = new Set(["A", "B", "C", "D", "E", "F"]);
const DEFAULT_WORD_NUMBER = "50";
const VALID_WORD_NUMBERS = new Set(["50", "100", "200", "300"]);

function normalizeLevelSelection(level: string | undefined, language?: string): string {
  const normalized = (level ?? "").trim().toUpperCase();
  if (isLevelAllowedForLanguage(normalized, language)) {
    return normalized;
  }
  return DEFAULT_LEVEL;
}

function normalizeTenseSelection(tense?: string): string {
  const normalized = (tense ?? "").trim().toUpperCase();
  if (VALID_TENSES.has(normalized)) {
    return normalized;
  }
  return DEFAULT_TENSE;
}

function normalizeWordNumberSelection(wordNumber?: string): string {
  const normalized = (wordNumber ?? "").trim();
  if (VALID_WORD_NUMBERS.has(normalized)) {
    return normalized;
  }
  return DEFAULT_WORD_NUMBER;
}

function normalizeImageReadingSelectionBlocks(
  selectionBlocks: ImageReadingSelectionBlock[],
): ImageReadingSelectionBlock[] {
  return selectionBlocks
    .map((block) => {
      const x = Number.isFinite(block.x)
        ? Math.min(Math.max(0, block.x), 0.9999)
        : 0;
      const y = Number.isFinite(block.y)
        ? Math.min(Math.max(0, block.y), 0.9999)
        : 0;
      const maxWidth = Math.max(0.0001, 1 - x);
      const maxHeight = Math.max(0.0001, 1 - y);
      const width = Number.isFinite(block.width)
        ? Math.min(Math.max(0.0001, block.width), maxWidth)
        : maxWidth;
      const height = Number.isFinite(block.height)
        ? Math.min(Math.max(0.0001, block.height), maxHeight)
        : maxHeight;
      return {
        x: Number(x.toFixed(4)),
        y: Number(y.toFixed(4)),
        width: Number(width.toFixed(4)),
        height: Number(height.toFixed(4)),
      };
    })
    .filter((block) => block.width > 0 && block.height > 0);
}

function isAuthRequiredError(error: unknown): boolean {
  return error instanceof Error && error.message === "AUTH_REQUIRED";
}

export function isValidArticleUrl(value: string): boolean {
  try {
    const parsed = new URL(value.trim());
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

function getImageReadingCandidateRejectionReasonLabel(
  translate: (key: string) => string,
  reason?: ImageReadingArticleCandidateDiagnostic["rejection_reason"],
): string {
  switch (reason) {
    case "outside_image_bounds":
      return translate("image_reading_candidate_rejection_outside_image_bounds");
    case "confidence_too_low_for_single_article":
      return translate("image_reading_candidate_rejection_low_confidence");
    case "missing_geometry":
    default:
      return translate("image_reading_candidate_rejection_missing_geometry");
  }
}

interface FormValues {
  typ: string;
  language: string;
  level: string;
  tense: string;
  word_number: string;
  main_character: string;
  topic: string;
  image: FileList;
}

type PhotoIntentStep = "hidden" | "intent" | "mode" | "select_article";
type PhotoIntentChoice =
  | "text"
  | "import_vocab"
  | "import_text"
  | "adapt_to_level";
type ImageReadingWorkflowState =
  | "idle"
  | "action_selection"
  | "detecting_articles"
  | "article_selection"
  | "article_confirm"
  | "processing_selected_article"
  | "done"
  | "error";
type ImageReadingErrorStage = "detecting_articles" | "processing_selected_article";
type PhotoImportState =
  | "idle"
  | "creating"
  | "processing"
  | "review"
  | "complete"
  | "error";

const INTEREST_MAP_PREFILL_STORAGE_KEY = "interest-map-prefill-topic";

export function GenerateTextCard() {
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const navigate = useNavigate();
  const { languageSpace } = useLanguageSpace("ENGLISH");
  const activeLanguage =
    languageSpace === "ALL"
      ? TEXT_LANGUAGES[0]?.id ?? "ENGLISH"
      : languageSpace;

  const [showTools, setShowTools] = useState(false);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const createGeneratedText = useGeneratedTextCreate();
  const createImageReadingText = useGeneratedTextCreateFromImage();
  const createArticleReadingText = useGeneratedTextCreateFromArticleUrl();
  const { translate } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const lastHydratedDefaultsRef = useRef<string | null>(null);
  const [selectedTab, setSelectedTab] = useState<
    "tense" | "level" | "wordNumber"
  >("tense");
  const [photoModalStep, setPhotoModalStep] =
    useState<PhotoIntentStep>("hidden");
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);
  const [pendingImageReadingWorkflow, setPendingImageReadingWorkflow] =
    useState<ImageReadingTextWorkflow | null>(null);
  const [imageReadingLayout, setImageReadingLayout] =
    useState<ImageReadingLayoutAnalysis | null>(null);
  const [imageReadingWorkflowState, setImageReadingWorkflowState] =
    useState<ImageReadingWorkflowState>("idle");
  const [imageReadingErrorStage, setImageReadingErrorStage] =
    useState<ImageReadingErrorStage>("detecting_articles");
  const [imageReadingErrorMessage, setImageReadingErrorMessage] =
    useState<string | null>(null);
  const [lastImageReadingSelectionBlocks, setLastImageReadingSelectionBlocks] =
    useState<ImageReadingSelectionBlock[]>([]);
  const [selectedArticleCandidateId, setSelectedArticleCandidateId] = useState<
    string | null
  >(null);
  const [photoImportState, setPhotoImportState] =
    useState<PhotoImportState>("idle");
  const [activePhotoImportId, setActivePhotoImportId] = useState<
    number | null
  >(null);
  const [photoImportMode, setPhotoImportMode] =
    useState<PhotoVocabularyImportMode | null>(null);
  const [photoImportEntries, setPhotoImportEntries] = useState<
    PhotoVocabularyImportWord[]
  >([]);
  const [selectedTokenIds, setSelectedTokenIds] = useState<string[]>([]);
  const [selectedTargetLanguages, setSelectedTargetLanguages] = useState<
    string[]
  >([]);
  const [translationResults, setTranslationResults] = useState<
    {
      token_id: string;
      vocabulary_id?: number;
      translation_language: string;
      translation_word: string;
      translation_sentence: string;
    }[]
  >([]);
  const [photoImportError, setPhotoImportError] = useState<string | null>(null);
  const [isArticleImportOpen, setIsArticleImportOpen] = useState(false);
  const [articleUrlValue, setArticleUrlValue] = useState("");
  const [articleUrlError, setArticleUrlError] = useState<string | null>(null);
  const [clipboardArticleUrl, setClipboardArticleUrl] = useState<string | null>(null);
  const selectionHydratedRef = useRef(false);

  const [formDefaultsByLanguage, setFormDefaultsByLanguage] =
    useLocalStorage<FormDefaultsByLanguage>("per-language-create-settings-v1", {});
  const userSettings = useUserSettings({ enabled: true });
  const currentDefaults: FormDefaults = useMemo(
    () => {
      const storedDefaults = formDefaultsByLanguage[activeLanguage];
      const settingsLevel = normalizeLevelSelection(
        userSettings.data?.language_levels?.[activeLanguage],
        activeLanguage,
      );
      return {
        level:
          settingsLevel ??
          normalizeLevelSelection(storedDefaults?.level, activeLanguage),
        typ: DEFAULT_TEXT_TYPE,
        tense: normalizeTenseSelection(storedDefaults?.tense),
        word_number: normalizeWordNumberSelection(storedDefaults?.word_number),
        main_character: "",
        language: storedDefaults?.language ?? activeLanguage,
      };
    },
    [activeLanguage, formDefaultsByLanguage, userSettings.data?.language_levels],
  );
  const queryClient = useQueryClient();
  const createPhotoImport = usePhotoVocabularyImportCreate();
  const finalizePhotoImport = usePhotoVocabularyImportFinalize();
  const previewTranslations = usePhotoVocabularyImportPreviewTranslations();
  const imageReadingLayoutAnalysis = useImageReadingLayoutAnalysis();
  const photoImportQuery = usePhotoVocabularyImport(activePhotoImportId);
  const [readyTopicIdea, setReadyTopicIdea] = useState<TopicIdeaDto | null>(null);
  const [appliedTopicIdea, setAppliedTopicIdea] = useState<TopicIdeaDto | null>(
    null,
  );
  const topicIdeaNext = useTopicIdeaNext();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormValues>();
  const [topicValue] = watch(["topic"]);
  const [formLanguage] = watch(["language"]);
  const [formTyp] = watch(["typ", "level", "tense", "word_number"]);

  useEffect(() => {
    // Hydrate form controls when switching language spaces with per-language defaults.
    const nextLanguage = currentDefaults.language ?? activeLanguage;
    const nextLevel = currentDefaults.level ?? DEFAULT_LEVEL;
    const nextTyp = currentDefaults.typ ?? DEFAULT_TEXT_TYPE;
    const nextTense = currentDefaults.tense ?? DEFAULT_TENSE;
    const nextWordNumber = currentDefaults.word_number ?? DEFAULT_WORD_NUMBER;
    const nextMainCharacter = currentDefaults.main_character ?? "";

    const nextSnapshot = JSON.stringify([
      nextLanguage,
      nextLevel,
      nextTyp,
      nextTense,
      nextWordNumber,
      nextMainCharacter,
    ]);
    if (lastHydratedDefaultsRef.current === nextSnapshot) {
      return;
    }
    lastHydratedDefaultsRef.current = nextSnapshot;

    setValue("language", nextLanguage);
    setValue("level", nextLevel);
    setValue("typ", nextTyp);
    setValue("tense", nextTense);
    setValue("word_number", nextWordNumber);
    setValue("main_character", nextMainCharacter);
  }, [
    activeLanguage,
    currentDefaults.language,
    currentDefaults.level,
    currentDefaults.typ,
    currentDefaults.tense,
    currentDefaults.word_number,
    currentDefaults.main_character,
    setValue,
  ]);

  useEffect(() => {
    const prefilledTopic = sessionStorage.getItem(INTEREST_MAP_PREFILL_STORAGE_KEY);
    if (!prefilledTopic?.trim()) {
      return;
    }
    applyTopicSuggestion(prefilledTopic);
    sessionStorage.removeItem(INTEREST_MAP_PREFILL_STORAGE_KEY);
  }, []);

  const topicRegister = register("topic", {
    required: false,
  });
  const photoImportLoading =
    photoImportState === "creating" || photoImportState === "processing";
  const articleImportLoading = createArticleReadingText.isPending;
  const finalizeDisabled =
    selectedTokenIds.length === 0 ||
    translationResults.length === 0 ||
    finalizePhotoImport.isPending;
  useEffect(() => {
    const data = photoImportQuery.data;
    if (!data) {
      return;
    }
    if (data.status === "COMPLETE") {
      if (!selectionHydratedRef.current) {
        const normalizedEntries = (data.result_payload ?? []).map(
          (entry, index) => ({
            ...entry,
            token_id: entry.token_id || `${Date.now()}-${index}`,
          }),
        );
        setPhotoImportEntries(normalizedEntries);
        setSelectedTokenIds(
          normalizedEntries.map((entry) => entry.token_id),
        );
        selectionHydratedRef.current = true;
        setPhotoImportError(null);
        if (
          photoImportState !== "review" &&
          photoImportState !== "complete"
        ) {
          setPhotoImportState("review");
        }
        if (selectedTargetLanguages.length === 0) {
          const entryLangs = normalizedEntries
            .map((e) => (e.language || "").trim())
            .filter(Boolean);
          const uniqueEntryLangs = Array.from(new Set(entryLangs));
          const defaultLang =
            formLanguage && formLanguage !== "unselected"
              ? formLanguage
              : undefined;
          if (
            userSettings.data?.mother_tongue &&
            uniqueEntryLangs.length > 0 &&
            uniqueEntryLangs.every(
              (lang) => lang !== userSettings.data?.mother_tongue,
            )
          ) {
            setSelectedTargetLanguages([userSettings.data.mother_tongue]);
          } else if (defaultLang) {
            setSelectedTargetLanguages([defaultLang]);
          }
        }
      }
    } else if (data.status === "FAILED" && photoImportState !== "error") {
      setPhotoImportState("error");
      setPhotoImportEntries([]);
      setSelectedTokenIds([]);
      setPhotoImportError(
        data.error_message || translate("photo_import_no_words"),
      );
      selectionHydratedRef.current = false;
    }
  }, [
    photoImportQuery.data,
    photoImportState,
    translate,
    selectedTargetLanguages.length,
    formLanguage,
    userSettings.data?.mother_tongue,
  ]);

  useEffect(() => {
    if (!isArticleImportOpen || typeof navigator === "undefined" || !navigator.clipboard) {
      return;
    }
    let cancelled = false;
    void navigator.clipboard
      .readText()
      .then((value) => {
        if (cancelled) {
          return;
        }
        const trimmed = value.trim();
        setClipboardArticleUrl(isValidArticleUrl(trimmed) ? trimmed : null);
      })
      .catch(() => {
        if (!cancelled) {
          setClipboardArticleUrl(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isArticleImportOpen]);

  const handleImageSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0] ?? null;
    if (!selectedFile) {
      return;
    }
    if (
      photoImportState !== "idle" ||
      createImageReadingText.isPending ||
      imageReadingLayoutAnalysis.isPending
    ) {
      notifySnackbar({
        message: translate("photo_import_in_progress"),
      });
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }
    let file = selectedFile;
    try {
      file = await prepareImageUploadFile(selectedFile);
    } catch (error) {
      console.warn("Falling back to the original selected image.", error);
    }
    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(file);
    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl);
    }
    const url = URL.createObjectURL(file);
    setImagePreviewUrl(url);
    setValue("image", dataTransfer.files);
    setPendingImageFile(file);
    setPhotoModalStep("intent");
    setImageReadingWorkflowState("action_selection");
    setPhotoImportEntries([]);
    setSelectedTokenIds([]);
    setSelectedTargetLanguages([]);
    setTranslationResults([]);
    setActivePhotoImportId(null);
    setPhotoImportMode(null);
    setPhotoImportError(null);
    setPendingImageReadingWorkflow(null);
    setImageReadingLayout(null);
    setImageReadingWorkflowState("idle");
    setImageReadingErrorMessage(null);
    setLastImageReadingSelectionBlocks([]);
    setSelectedArticleCandidateId(null);
    selectionHydratedRef.current = false;
  };

  const handleImageDelete = () => {
    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl);
    }
    setImagePreviewUrl(null);
    setPendingImageFile(null);
    setPendingImageReadingWorkflow(null);
    setImageReadingLayout(null);
    setImageReadingWorkflowState("idle");
    setImageReadingErrorMessage(null);
    setLastImageReadingSelectionBlocks([]);
    setSelectedArticleCandidateId(null);
    setPhotoModalStep("hidden");
    // Clear form value
    setValue("image", new DataTransfer().files as FileList);
    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const submitArticleImport = (urlValue: string) => {
    const trimmedUrl = urlValue.trim();
    if (!isValidArticleUrl(trimmedUrl)) {
      setArticleUrlError(translate("article_import_invalid_url"));
      return;
    }
    setArticleUrlError(null);
    void createArticleReadingText
      .mutateAsync({
        url: trimmedUrl,
        language:
          formLanguage && formLanguage !== "unselected"
            ? formLanguage
            : activeLanguage,
      })
      .then((created) => {
        setArticleUrlValue("");
        setClipboardArticleUrl(null);
        setIsArticleImportOpen(false);
        if (created?.id) {
          notifySnackbar({ message: translate("generated_text_queued") });
          navigate({
            to: "/app/reeprise/$generatedTextId",
            params: { generatedTextId: `${created.id}` },
            search: {
              chapter: undefined,
              focusWord: undefined,
              focusSentence: undefined,
              focusToken: undefined,
              jumpTo: undefined,
            },
          });
        }
      })
      .catch((error: unknown) => {
        if (isAuthRequiredError(error)) {
          notifySnackbar({ message: translate("auth_session_expired") });
          void queryClient.invalidateQueries({
            queryKey: [LOGIN_STATUS_QUERY_KEY],
          });
          return;
        }
        setArticleUrlError(
          error instanceof Error && error.message
            ? error.message
          : translate("article_import_generic_error"),
        );
      });
  };

  const handleArticleImportSubmit = () => {
    submitArticleImport(articleUrlValue);
  };

  const handlePrimarySubmit: SubmitHandler<FormValues> = (data) => {
    const topic = data.topic.trim();
    if (isValidArticleUrl(topic)) {
      submitArticleImport(topic);
      return;
    }
    void onSubmit({
      ...data,
      topic: data.topic,
    });
  };

  const handlePhotoIntentChoice = (choice: PhotoIntentChoice) => {
    if (choice === "text") {
      setPhotoModalStep("hidden");
      setPendingImageFile(null);
      return;
    }
    if (choice === "import_vocab") {
      setPhotoModalStep("mode");
      return;
    }
    setPhotoModalStep("select_article");
    void prepareImageReadingWorkflow(
      choice === "import_text" ? "IMPORT_TEXT" : "ADAPT_TO_LEVEL",
    );
  };

  const resetPhotoImportFlow = () => {
    setPhotoImportState("idle");
    setActivePhotoImportId(null);
    setPhotoImportEntries([]);
    setSelectedTokenIds([]);
    setSelectedTargetLanguages([]);
    setTranslationResults([]);
    setPhotoImportMode(null);
    setPhotoImportError(null);
    setPendingImageReadingWorkflow(null);
    setImageReadingLayout(null);
    setImageReadingWorkflowState("idle");
    setImageReadingErrorMessage(null);
    setLastImageReadingSelectionBlocks([]);
    setSelectedArticleCandidateId(null);
    setPhotoModalStep("hidden");
    selectionHydratedRef.current = false;
  };

  const startPhotoImport = async (mode: PhotoVocabularyImportMode) => {
    if (!pendingImageFile) {
      notifySnackbar({ message: translate("photo_import_no_words") });
      return;
    }
    setPhotoImportState("creating");
    setPhotoImportError(null);
    selectionHydratedRef.current = false;
    try {
      const record = await createPhotoImport.mutateAsync({
        file: pendingImageFile,
        mode,
      });
      setPhotoImportMode(mode);
      setActivePhotoImportId(record.id);
      setPhotoImportState("processing");
      setPhotoModalStep("hidden");
      setPendingImageFile(null);
      handleImageDelete();
      const defaultLang =
        formLanguage && formLanguage !== "unselected"
          ? formLanguage
          : undefined;
      setSelectedTargetLanguages(defaultLang ? [defaultLang] : []);
    } catch (error) {
      console.error(error);
      setPhotoImportState("error");
      setPhotoImportError(
        error instanceof Error
          ? error.message
          : translate("photo_import_error_title"),
      );
    }
  };

  const startImageReadingWorkflow = async (
    workflow: ImageReadingTextWorkflow,
    selectionBlocks: ImageReadingSelectionBlock[] = [],
  ) => {
    if (!pendingImageFile) {
      notifySnackbar({ message: translate("photo_import_no_words") });
      return;
    }
    try {
      const normalizedSelectionBlocks =
        normalizeImageReadingSelectionBlocks(selectionBlocks);
      setImageReadingErrorStage("processing_selected_article");
      setImageReadingErrorMessage(null);
      setLastImageReadingSelectionBlocks(normalizedSelectionBlocks);
      setImageReadingWorkflowState("processing_selected_article");
      const created = await createImageReadingText.mutateAsync({
        file: pendingImageFile,
        language:
          formLanguage && formLanguage !== "unselected"
            ? formLanguage
            : activeLanguage,
        textType: formTyp && formTyp !== "unselected" ? formTyp : DEFAULT_TEXT_TYPE,
        workflow,
        selectionBlocks: normalizedSelectionBlocks,
      });
      setPhotoModalStep("hidden");
      setPendingImageFile(null);
      setPendingImageReadingWorkflow(null);
      setImageReadingLayout(null);
      setImageReadingWorkflowState("done");
      setImageReadingErrorMessage(null);
      setLastImageReadingSelectionBlocks([]);
      setSelectedArticleCandidateId(null);
      handleImageDelete();
      if (created?.id) {
        navigate({
          to: "/app/reeprise/$generatedTextId",
          params: { generatedTextId: `${created.id}` },
          search: {
            chapter: undefined,
            focusWord: undefined,
            focusSentence: undefined,
            focusToken: undefined,
            jumpTo: undefined,
          },
        });
      }
    } catch (error) {
      setImageReadingWorkflowState("error");
      setImageReadingErrorStage("processing_selected_article");
      if (isAuthRequiredError(error)) {
        notifySnackbar({ message: translate("auth_session_expired") });
        void queryClient.invalidateQueries({
          queryKey: [LOGIN_STATUS_QUERY_KEY],
        });
        return;
      }
      setImageReadingErrorMessage(
        error instanceof Error && error.message
          ? error.message
          : translate("image_reading_processing_error"),
      );
      notifySnackbar({
        message: translate("generate_text_failed"),
      });
    }
  };

  const prepareImageReadingWorkflow = async (
    workflow: ImageReadingTextWorkflow,
  ) => {
    if (!pendingImageFile) {
      notifySnackbar({ message: translate("photo_import_no_words") });
      return;
    }
    try {
      setPendingImageReadingWorkflow(workflow);
      setPhotoModalStep("select_article");
      setImageReadingWorkflowState("detecting_articles");
      setImageReadingErrorStage("detecting_articles");
      setImageReadingErrorMessage(null);
      const layout = await imageReadingLayoutAnalysis.mutateAsync({
        file: pendingImageFile,
      });
      setImageReadingLayout(layout);
      const candidates = layout.candidates ?? [];
      const firstCandidate = candidates[0];
      if (
        candidates.length === 1 &&
        firstCandidate &&
        firstCandidate.confidence >= 0.75 &&
        layout.detection_status === "single"
      ) {
        await startImageReadingWorkflow(
          workflow,
          firstCandidate.blocks?.length ? firstCandidate.blocks : [firstCandidate.bbox],
        );
        return;
      }
      setSelectedArticleCandidateId(null);
      setImageReadingWorkflowState("article_selection");
      setPhotoModalStep("select_article");
    } catch (error) {
      setImageReadingWorkflowState("error");
      setImageReadingErrorStage("detecting_articles");
      setImageReadingErrorMessage(translate("image_reading_selection_error"));
      setPhotoModalStep("select_article");
      if (isAuthRequiredError(error)) {
        notifySnackbar({ message: translate("auth_session_expired") });
        void queryClient.invalidateQueries({
          queryKey: [LOGIN_STATUS_QUERY_KEY],
        });
        return;
      }
      notifySnackbar({
        message: translate("generate_text_failed"),
      });
    }
  };

  const toggleTokenSelection = (tokenId: string) => {
    setSelectedTokenIds((prev) =>
      prev.includes(tokenId)
        ? prev.filter((id) => id !== tokenId)
        : [...prev, tokenId],
    );
    setTranslationResults([]);
  };

  const selectAllTokens = () => {
    setSelectedTokenIds(
      photoImportEntries
        .map((entry) => entry.token_id)
        .filter((tokenId): tokenId is string => Boolean(tokenId)),
    );
    setTranslationResults([]);
  };

  const clearTokenSelection = () => {
    setSelectedTokenIds([]);
    setTranslationResults([]);
  };

  const toggleTargetLanguage = (lang: string) => {
    setSelectedTargetLanguages((prev) =>
      prev.includes(lang) ? prev.filter((id) => id !== lang) : [...prev, lang],
    );
    setTranslationResults([]);
  };

  const handleFinalizeImport = async () => {
    if (
      !activePhotoImportId ||
      selectedTokenIds.length === 0
    ) {
      return;
    }
    try {
      const result = await finalizePhotoImport.mutateAsync({
        importId: activePhotoImportId,
        tokenIds: selectedTokenIds,
        targetLanguages: selectedTargetLanguages,
        learningLanguage:
          formLanguage && formLanguage !== "unselected"
            ? formLanguage
            : undefined,
      });
      notifySnackbar({
        message: translate("photo_import_finalize_success"),
      });
      invalidateVocabularyListQuery(queryClient);
      setTranslationResults(result.translations ?? []);
      setPhotoImportState("complete");
      selectionHydratedRef.current = false;
    } catch (error) {
      console.error(error);
      setPhotoImportState("error");
      setPhotoImportError(
        error instanceof Error
          ? error.message
          : translate("photo_import_error_title"),
      );
    }
  };

  const handlePreviewTranslations = async () => {
    if (!activePhotoImportId || selectedTokenIds.length === 0) {
      return;
    }
    try {
      const result = await previewTranslations.mutateAsync({
        importId: activePhotoImportId,
        tokenIds: selectedTokenIds,
        targetLanguages: selectedTargetLanguages,
        learningLanguage:
          formLanguage && formLanguage !== "unselected"
            ? formLanguage
            : undefined,
      });
      setTranslationResults(result.translations ?? []);
    } catch (error) {
      console.error(error);
      notifySnackbar({
        message:
          error instanceof Error
            ? error.message
            : translate("photo_import_error_title"),
      });
    }
  };

  const photoImportSelectionSet = useMemo(
    () => new Set(selectedTokenIds),
    [selectedTokenIds],
  );

  const applyTopicSuggestion = (topic: string) => {
    setValue("topic", topic, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    inputRef.current?.focus();
  };

  const fetchTopicIdea = async () => {
    return topicIdeaNext.mutateAsync({
      language:
        formLanguage && formLanguage !== "unselected"
          ? formLanguage
          : activeLanguage,
      text_type: formTyp && formTyp !== "unselected" ? formTyp : undefined,
    });
  };

  const handleRandomTopicSuggestion = () => {
    const useIdea = readyTopicIdea;
    const applyIdea = (idea: TopicIdeaDto) => {
      applyTopicSuggestion(idea.topic);
      setAppliedTopicIdea(idea);
      setReadyTopicIdea(null);
    };

    if (useIdea) {
      applyIdea(useIdea);
      return;
    }

    void fetchTopicIdea()
      .then((idea) => {
        applyIdea(idea);
      })
      .catch((error: unknown) => {
        if (isAuthRequiredError(error)) {
          notifySnackbar({ message: translate("auth_session_expired") });
          void queryClient.invalidateQueries({
            queryKey: [LOGIN_STATUS_QUERY_KEY],
          });
          return;
        }
        notifySnackbar({ message: translate("topic_surprise_me_failed") });
      });
  };

  const topicIdeaLoading = topicIdeaNext.isPending;

  const onSubmit: SubmitHandler<FormValues> = (data) => {
    console.log(JSON.stringify(data));
    const normalizedInputTopic = data.topic.trim();
    const usedSuggestedTopic =
      appliedTopicIdea && normalizedInputTopic === appliedTopicIdea.topic.trim()
        ? appliedTopicIdea
        : null;
    void createGeneratedText
      .mutateAsync({
        createReepriseDto: {
          text_type: data.typ,
          language: data.language,
          level: data.level,
          tense: data.tense,
          word_number: Number(data.word_number),
          topic: data.topic,
          main_character: "",
          topic_source: usedSuggestedTopic ? "IDEA" : "MANUAL",
          topic_suggestion_id: usedSuggestedTopic?.id,
          image: data.image,
        },
      })
      .then((created) => {
        setFormDefaultsByLanguage((prev) => {
          return {
            ...prev,
            [activeLanguage]: {
              main_character: "",
              level: data.level,
              tense: data.tense,
              language: activeLanguage,
              typ: data.typ,
              word_number: data.word_number,
            },
          };
        });
        // Clear topic field and image after successful generation
        setValue("topic", "");
        setAppliedTopicIdea(null);
        setReadyTopicIdea(null);
        handleImageDelete();
        if (created?.id) {
          notifySnackbar({ message: translate("generated_text_queued") });
          navigate({
            to: "/app/reeprise/$generatedTextId",
            params: { generatedTextId: `${created.id}` },
            search: {
              chapter: undefined,
              focusWord: undefined,
              focusSentence: undefined,
              focusToken: undefined,
              jumpTo: undefined,
            },
          });
        }
      })
      .catch((error: unknown) => {
        if (isAuthRequiredError(error)) {
          notifySnackbar({ message: translate("auth_session_expired") });
          void queryClient.invalidateQueries({
            queryKey: [LOGIN_STATUS_QUERY_KEY],
          });
          return;
        }
        notifySnackbar({
          message: translate("generate_text_failed"),
        });
      });
  };

  const onError = () => {
    if (errors.typ) {
      notifySnackbar({
        message: translate("form_error_text_type_required"),
      });
    }
    const hasRequiredFieldErrors =
      errors.language ||
      errors.level ||
      errors.tense ||
      errors.typ ||
      errors.word_number;
    if (hasRequiredFieldErrors && showTools) {
      setShowTools(false);
    }
  };

  const [levelFormFieldValue] = watch(["level"]);
  // On first render level can be undefined; fall back to per-language defaults.
  const selectedLevel = levelFormFieldValue ?? currentDefaults.level;
  let languageOptions = [] as string[];
  if (selectedLevel === "A0") {
    languageOptions = ["A"];
  } else if (selectedLevel === "A1") {
    languageOptions = ["A", "C", "E"];
  } else if (selectedLevel === "A2") {
    languageOptions = ["A", "B", "C", "E"];
  } else if (selectedLevel === "B1") {
    languageOptions = ["A", "B", "C", "D"];
  } else if (selectedLevel === "B2") {
    languageOptions = ["A", "B", "C", "D", "E", "F"];
  } else if (selectedLevel === "C1") {
    languageOptions = ["A", "B", "C", "D", "E", "F"];
  } else if (selectedLevel === "C2") {
    languageOptions = ["A", "B", "C", "D", "E", "F"];
  } else if (selectedLevel === "THINKERA") {
    languageOptions = ["A", "B", "C", "D", "E", "F"];
  } else {
    languageOptions = ["A", "B", "C", "E"];
  }
  const levelOptions = getLevelOptionsForLanguage(formLanguage ?? activeLanguage);

  // Cleanup image preview URL on unmount
  useEffect(() => {
    return () => {
      if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
    };
  }, [imagePreviewUrl]);

  useEffect(() => {
    const textarea = inputRef.current;
    if (!textarea) {
      return;
    }
    textarea.style.height = "auto";
    const nextHeight = Math.max(textarea.scrollHeight, 28);
    textarea.style.height = `${nextHeight}px`;
  }, [topicValue]);

  return (
    <form onSubmit={handleSubmit(handlePrimarySubmit, onError)} className="w-full px-0">
      <div
        data-testid="generate-text-card"
        className="flex w-full flex-col rounded-lg bg-[#f8fafc] px-4 pt-4 pb-2 text-slate-900"
      >
        <div className="flex items-center gap-3">
          <textarea
            {...topicRegister}
            ref={(el) => {
              topicRegister.ref(el);
              inputRef.current = el;
            }}
            rows={1}
            placeholder={translate("form_topic_placeholder")}
            className="min-h-[28px] flex-1 resize-none overflow-hidden border-none bg-transparent text-[1.05rem] leading-6 placeholder:text-slate-400 focus:outline-none"
          />
          {imagePreviewUrl && (
            <div className="flex items-center gap-2 pr-1 pt-0.5">
              <img
                src={imagePreviewUrl}
                alt="preview"
                className="h-9 w-9 rounded-sm border border-slate-200 object-cover"
              />
              <button
                type="button"
                onClick={handleImageDelete}
                className="rounded-full p-1 text-slate-500 transition hover:text-rose-600"
              >
                <XMarkIcon className="h-3 w-3" />
              </button>
            </div>
          )}
          <button
            type="submit"
            className={`inline-flex h-10 w-10 shrink-0 -translate-y-0.5 items-center justify-center rounded-full border transition ${
              topicValue?.trim() || imagePreviewUrl
                ? "border-transparent bg-transparent text-slate-900"
                : "border-transparent bg-transparent text-slate-300"
            }`}
            aria-label={translate("topic_surprise_me")}
            title={translate("topic_surprise_me")}
          >
            <ArrowRightIcon className="h-6 w-6" />
          </button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleImageSelect}
        />
        <input type="hidden" {...register("language")} />
        <input
          type="hidden"
          {...register("typ", {
            required: true,
            validate: (value) => value !== "unselected",
          })}
        />
        <input type="hidden" {...register("main_character")} />

        <div className="mt-0 flex items-center gap-2 pt-1 pb-1">
          <div className="flex min-w-0 flex-1 items-center gap-2 overflow-x-auto text-slate-500">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-transparent transition hover:text-slate-700"
              aria-label={translate("photo_import_prompt_generate_text")}
              title={translate("photo_import_prompt_generate_text")}
            >
              <CameraIcon className="h-6 w-6" />
            </button>
            <button
              type="button"
              data-testid="generate-text-tools-toggle"
              className={`inline-flex h-10 w-10 items-center justify-center rounded-full bg-transparent transition ${
                showTools
                  ? "text-slate-900"
                  : "text-slate-500 hover:text-slate-700"
              }`}
              onClick={() => setShowTools(!showTools)}
            >
              <AdjustmentsHorizontalIcon className="h-6 w-6" />
            </button>
            <button
              type="button"
              data-testid="generate-text-idea-button"
              onClick={handleRandomTopicSuggestion}
              disabled={topicIdeaLoading}
              className="inline-flex h-10 shrink-0 items-center gap-1 rounded-full bg-transparent px-2.5 text-xs font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-wait disabled:bg-transparent disabled:text-slate-400 sm:px-3 sm:text-sm"
              aria-label={translate("topic_surprise_me")}
              title={translate("topic_surprise_me")}
            >
              <SparklesIcon className={`h-5 w-5 ${topicIdeaLoading ? "animate-pulse" : ""}`} />
              <span>{topicIdeaLoading ? translate("topic_surprise_me_loading") : translate("topic_surprise_me_short")}</span>
            </button>
          </div>
        </div>
        {topicIdeaLoading ? (
          <div
            className="mt-4 overflow-hidden rounded-[1.25rem] border border-slate-200 bg-[linear-gradient(90deg,#f8fafc_0%,#eef2ff_50%,#f8fafc_100%)] p-4"
            data-testid="generate-text-idea-loading"
            aria-live="polite"
            aria-busy="true"
          >
            <div className="flex items-center gap-3">
              <div className="relative flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white">
                <div className="h-2.5 w-2.5 rounded-full bg-sky-400/90 animate-pulse" />
                <div className="absolute inset-2 rounded-full border border-sky-100" />
              </div>
              <div className="min-w-0 flex-1 space-y-2">
                <div className="h-3.5 w-24 rounded-full bg-slate-200/90 animate-pulse" />
                <div className="h-4 w-[82%] rounded-full bg-slate-200/80 animate-pulse" />
                <div className="h-4 w-[58%] rounded-full bg-slate-200/70 animate-pulse" />
              </div>
            </div>
            <div className="mt-3 h-2.5 w-32 rounded-full bg-sky-200/70 animate-pulse" />
          </div>
        ) : null}
        {isArticleImportOpen && (
          <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
            <label
              htmlFor="article-import-url"
              className="block text-sm font-medium text-slate-800"
            >
              {translate("article_import_label")}
            </label>
            <input
              id="article-import-url"
              data-testid="article-import-url-input"
              type="url"
              inputMode="url"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              placeholder={translate("article_import_placeholder")}
              value={articleUrlValue}
              onChange={(event) => {
                setArticleUrlValue(event.target.value);
                if (articleUrlError) {
                  setArticleUrlError(null);
                }
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  handleArticleImportSubmit();
                }
              }}
              className="mt-2 w-full rounded-2xl border border-slate-300 px-4 py-3 text-base text-slate-900 outline-none focus:border-slate-500"
            />
            {clipboardArticleUrl && clipboardArticleUrl !== articleUrlValue.trim() && (
              <button
                type="button"
                className="mt-2 text-sm text-slate-600 underline"
                onClick={() => {
                  setArticleUrlValue(clipboardArticleUrl);
                  setArticleUrlError(null);
                }}
              >
                {translate("article_import_use_copied_link")}
              </button>
            )}
            {articleUrlError && (
              <p className="mt-2 text-sm text-rose-700">{articleUrlError}</p>
            )}
            <div className="mt-3 flex items-center justify-between gap-3">
              <button
                type="button"
                className="rounded-full border border-slate-300 px-4 py-2 text-sm text-slate-700"
                onClick={() => {
                  setIsArticleImportOpen(false);
                  setArticleUrlError(null);
                }}
              >
                {translate("article_import_cancel")}
              </button>
              <button
                type="button"
                data-testid="article-import-submit"
                className="rounded-full bg-slate-900 px-4 py-2 text-sm text-white disabled:bg-slate-300"
                onClick={handleArticleImportSubmit}
                disabled={articleImportLoading}
              >
                {articleImportLoading
                  ? translate("article_import_loading_reading")
                  : translate("article_import_submit")}
              </button>
            </div>
          </div>
        )}
        <div className={`${showTools ? "mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4" : "hidden"}`}>
          <div>
            <div className="flex flex-wrap gap-4 text-sm font-medium text-slate-700">
              {[
                { key: "level", label: translate("form_level") },
                { key: "wordNumber", label: translate("form_wordcount_label") },
                { key: "tense", label: translate("form_tense") },
              ].map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  data-testid={`generate-text-tab-${tab.key}`}
                  onClick={() =>
                    setSelectedTab(tab.key as "tense" | "level" | "wordNumber")
                  }
                  className={`pb-1 border-b-2 min-w-fit transition-all duration-200 ${
                    selectedTab === tab.key
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <div className={selectedTab === "level" ? "" : "hidden"}>
              <MySelect
                defaultValue={currentDefaults.level ?? DEFAULT_LEVEL}
                testId="generate-text-level-select"
                optionTestIdPrefix="generate-text-level-option"
                options={levelOptions.map((level) => [
                  level,
                  `${level} · ${translate(getLevelTranslationKey(level))}`,
                ])}
                {...register("level", {
                  required: true,
                  validate: (value) => value !== "unselected",
                })}
              />
            </div>
            <div className={selectedTab === "tense" ? "" : "hidden"}>
              <MySelect
                options={[
                  ["A", translate("form_tense_present")],
                  ["B", translate("form_tense_past")],
                  ["C", translate("form_tense_present_perfect")],
                  ["D", translate("form_tense_past_perfect")],
                  ["E", translate("form_tense_future_I")],
                  ["F", translate("form_tense_future_perfect")],
                ].filter((v): v is [string, string] =>
                  languageOptions.includes(v[0]),
                )}
                defaultValue={currentDefaults.tense ?? DEFAULT_TENSE}
                {...register("tense", {
                  required: true,
                  validate: (value) => value !== "unselected",
                })}
              />
            </div>
            <div className={selectedTab === "wordNumber" ? "" : "hidden"}>
              <MySelect
                options={[
                  ["50", "50"],
                  ["100", "100"],
                  ["200", "200"],
                  ["300", "300"],
                ]}
                defaultValue={currentDefaults.word_number ?? DEFAULT_WORD_NUMBER}
                {...register("word_number", {
                  required: true,
                  validate: (value) => value !== "unselected",
                })}
              />
            </div>
          </div>
        </div>
      </div>
      {photoImportState !== "idle" && (
        <PhotoImportPanel
        translate={translate}
        state={photoImportState}
        entries={photoImportEntries}
        selectionSet={photoImportSelectionSet}
        onToggleToken={toggleTokenSelection}
          onSelectAll={selectAllTokens}
          onClearSelection={clearTokenSelection}
          onFinalize={handleFinalizeImport}
          onDismiss={resetPhotoImportFlow}
          finalizeDisabled={finalizeDisabled}
          isFinalizing={finalizePhotoImport.isPending}
        mode={photoImportMode}
        loading={photoImportLoading}
        errorMessage={photoImportError}
        targetLanguages={selectedTargetLanguages}
        onToggleTargetLanguage={toggleTargetLanguage}
        availableLanguages={TEXT_LANGUAGES}
        translationResults={translationResults}
        onPreviewTranslations={handlePreviewTranslations}
        previewLoading={previewTranslations.isPending}
      />
      )}
      {photoModalStep !== "hidden" && pendingImageFile && (
        <PhotoIntentModal
          step={photoModalStep}
          onSelectIntent={handlePhotoIntentChoice}
          onSelectMode={startPhotoImport}
          onConfirmSelection={(selectionBlocks) => {
            if (!pendingImageReadingWorkflow) {
              return;
            }
            void startImageReadingWorkflow(
              pendingImageReadingWorkflow,
              selectionBlocks,
            );
          }}
          onClose={() => {
            setPhotoModalStep("hidden");
            setPendingImageFile(null);
            setPendingImageReadingWorkflow(null);
            setImageReadingLayout(null);
            setImageReadingWorkflowState("idle");
            setImageReadingErrorMessage(null);
            setLastImageReadingSelectionBlocks([]);
            setSelectedArticleCandidateId(null);
          }}
          translate={translate}
          onBack={() => setPhotoModalStep("intent")}
          workflowLoading={
            createImageReadingText.isPending ||
            imageReadingLayoutAnalysis.isPending
          }
          imagePreviewUrl={imagePreviewUrl}
          imageReadingLayout={imageReadingLayout}
          imageReadingWorkflowState={imageReadingWorkflowState}
          selectedArticleCandidateId={selectedArticleCandidateId}
          onSelectArticleCandidate={(candidateId) => {
            setSelectedArticleCandidateId(candidateId);
            setImageReadingWorkflowState("article_confirm");
          }}
          onChooseAnotherArticle={() => {
            setSelectedArticleCandidateId(null);
            setImageReadingWorkflowState("article_selection");
          }}
          onRetryArticleDetection={() => {
            if (!pendingImageReadingWorkflow) {
              return;
            }
            if (imageReadingErrorStage === "processing_selected_article") {
              void startImageReadingWorkflow(
                pendingImageReadingWorkflow,
                lastImageReadingSelectionBlocks,
              );
              return;
            }
            void prepareImageReadingWorkflow(pendingImageReadingWorkflow);
          }}
          imageReadingErrorMessage={imageReadingErrorMessage}
          imageReadingErrorStage={imageReadingErrorStage}
        />
      )}
    </form>
  );
}

function PhotoIntentModal(props: {
  step: PhotoIntentStep;
  onSelectIntent: (choice: PhotoIntentChoice) => void;
  onSelectMode: (mode: PhotoVocabularyImportMode) => void;
  onConfirmSelection: (selectionBlocks: ImageReadingSelectionBlock[]) => void;
  onClose: () => void;
  translate: (key: string) => string;
  onBack?: () => void;
  workflowLoading: boolean;
  imagePreviewUrl: string | null;
  imageReadingLayout: ImageReadingLayoutAnalysis | null;
  imageReadingWorkflowState: ImageReadingWorkflowState;
  selectedArticleCandidateId: string | null;
  onSelectArticleCandidate: (candidateId: string) => void;
  onChooseAnotherArticle: () => void;
  onRetryArticleDetection: () => void;
  imageReadingErrorMessage: string | null;
  imageReadingErrorStage: ImageReadingErrorStage;
}) {
  const {
    step,
    onSelectIntent,
    onSelectMode,
    onConfirmSelection,
    onClose,
    translate,
    onBack,
    workflowLoading,
    imagePreviewUrl,
    imageReadingLayout,
    imageReadingWorkflowState,
    selectedArticleCandidateId,
    onSelectArticleCandidate,
    onChooseAnotherArticle,
    onRetryArticleDetection,
    imageReadingErrorMessage,
    imageReadingErrorStage,
  } =
    props;
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 px-4 py-4 sm:items-center"
      onClick={onClose}
    >
      <div
        className={`w-full rounded-2xl bg-white p-6 shadow-2xl ${
          step === "select_article"
            ? "max-h-[calc(100vh-2rem)] max-w-5xl overflow-y-auto"
            : "max-w-md"
        }`}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-900">
            {step === "intent"
              ? translate("photo_import_prompt_title")
              : step === "mode"
                ? translate("photo_import_mode_title")
                : translate("image_reading_selection_title")}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1 text-slate-400 hover:text-slate-600"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        {step === "intent" ? (
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => onSelectIntent("text")}
              disabled={workflowLoading}
              className="flex w-full items-center gap-4 rounded-2xl border border-slate-200 p-4 text-left transition hover:border-slate-400"
            >
              <div className="rounded-full bg-slate-100 p-3 text-slate-600">
                <DocumentTextIcon className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">
                  {translate("photo_import_prompt_generate_text")}
                </p>
                <p className="text-xs text-slate-500">
                  {translate("photo_import_prompt_generate_text_desc")}
                </p>
              </div>
            </button>
            <button
              type="button"
              onClick={() => onSelectIntent("import_vocab")}
              disabled={workflowLoading}
              className="flex w-full items-center gap-4 rounded-2xl border border-slate-200 p-4 text-left transition hover:border-slate-400"
            >
              <div className="rounded-full bg-slate-100 p-3 text-slate-600">
                <SparklesIcon className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">
                  {translate("photo_import_prompt_import_vocab")}
                </p>
                <p className="text-xs text-slate-500">
                  {translate("photo_import_prompt_import_vocab_desc")}
                </p>
              </div>
            </button>
            <button
              type="button"
              onClick={() => onSelectIntent("import_text")}
              disabled={workflowLoading}
              className="flex w-full items-center gap-4 rounded-2xl border border-slate-200 p-4 text-left transition hover:border-slate-400"
            >
              <div className="rounded-full bg-slate-100 p-3 text-slate-600">
                <DocumentTextIcon className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">
                  {translate("photo_import_prompt_import_text")}
                </p>
                <p className="text-xs text-slate-500">
                  {translate("photo_import_prompt_import_text_desc")}
                </p>
              </div>
            </button>
            <button
              type="button"
              onClick={() => onSelectIntent("adapt_to_level")}
              disabled={workflowLoading}
              className="flex w-full items-center gap-4 rounded-2xl border border-slate-200 p-4 text-left transition hover:border-slate-400"
            >
              <div className="rounded-full bg-slate-100 p-3 text-slate-600">
                <SparklesIcon className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">
                  {translate("photo_import_prompt_adapt_text")}
                </p>
                <p className="text-xs text-slate-500">
                  {translate("photo_import_prompt_adapt_text_desc")}
                </p>
              </div>
            </button>
          </div>
        ) : step === "mode" ? (
          <div className="space-y-4">
            <button
              type="button"
              onClick={() => onSelectMode("ALL_WORDS")}
              className="flex w-full items-center gap-4 rounded-2xl border border-slate-200 p-4 text-left transition hover:border-slate-400"
            >
              <div className="rounded-full bg-blue-50 p-3 text-blue-600">
                <PhotoIcon className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">
                  {translate("photo_import_mode_all_words")}
                </p>
                <p className="text-xs text-slate-500">
                  {translate("photo_import_prompt_import_vocab_desc")}
                </p>
              </div>
            </button>
            <button
              type="button"
              onClick={() => onSelectMode("HIGHLIGHTED_ONLY")}
              className="flex w-full items-center gap-4 rounded-2xl border border-slate-200 p-4 text-left transition hover:border-slate-400"
            >
              <div className="rounded-full bg-amber-50 p-3 text-amber-600">
                <SparklesIcon className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">
                  {translate("photo_import_mode_highlighted_words")}
                </p>
                <p className="text-xs text-slate-500">
                  {translate("photo_import_prompt_import_vocab_desc")}
                </p>
              </div>
            </button>
            <button
              type="button"
              onClick={() => {
                onBack?.();
              }}
              className="text-sm font-semibold text-slate-500 hover:text-slate-700"
            >
              {translate("photo_import_mode_back")}
            </button>
          </div>
        ) : (
          <ImageReadingArticleSelectionStep
            imagePreviewUrl={imagePreviewUrl}
            layout={imageReadingLayout}
            loading={workflowLoading}
            onBack={onBack}
            onConfirm={onConfirmSelection}
            translate={translate}
            workflowState={imageReadingWorkflowState}
            selectedCandidateId={selectedArticleCandidateId}
            onSelectCandidate={onSelectArticleCandidate}
            onChooseAnother={onChooseAnotherArticle}
            onRetryArticleDetection={onRetryArticleDetection}
            errorMessage={imageReadingErrorMessage}
            errorStage={imageReadingErrorStage}
          />
        )}
      </div>
    </div>
  );
}

function ImageReadingArticleSelectionStep(props: {
  imagePreviewUrl: string | null;
  layout: ImageReadingLayoutAnalysis | null;
  loading: boolean;
  onBack?: () => void;
  onConfirm: (selectionBlocks: ImageReadingSelectionBlock[]) => void;
  translate: (key: string, variables?: Record<string, string | number>) => string;
  workflowState: ImageReadingWorkflowState;
  selectedCandidateId: string | null;
  onSelectCandidate: (candidateId: string) => void;
  onChooseAnother: () => void;
  onRetryArticleDetection: () => void;
  errorMessage: string | null;
  errorStage: ImageReadingErrorStage;
}) {
  const {
    imagePreviewUrl,
    layout,
    loading,
    onBack,
    onConfirm,
    translate,
    workflowState,
    selectedCandidateId,
    onSelectCandidate,
    onChooseAnother,
    onRetryArticleDetection,
    errorMessage,
    errorStage,
  } = props;
  const candidates = layout?.candidates ?? [];
  const selectedCandidate =
    candidates.find((candidate) => candidate.id === selectedCandidateId) ?? null;
  const candidateDiagnostics = layout?.candidate_diagnostics ?? [];
  const rejectedDiagnostics = candidateDiagnostics.filter(
    (entry) => entry.status === "rejected",
  );
  const [manualSelectionEnabled, setManualSelectionEnabled] = useState(false);

  const blocksForCandidate = (
    candidate: { bbox: ImageReadingSelectionBlock; blocks?: ImageReadingArticleCandidateBlock[] },
  ) => (candidate.blocks?.length ? candidate.blocks : [candidate.bbox]);

  const handleCandidateSelect = (
    candidate: {
      id: string;
      title: string;
      preview: string;
    },
  ) => {
    console.log("clicked candidate", candidate.id, candidate.title);
    onSelectCandidate(candidate.id);
  };

  const handleConfirmSelectedCandidate = () => {
    if (!selectedCandidate) {
      return;
    }
    console.log(
      "processing candidate",
      selectedCandidate.id,
      selectedCandidate.title,
      selectedCandidate.preview,
    );
    onConfirm(blocksForCandidate(selectedCandidate));
  };

  useEffect(() => {
    setManualSelectionEnabled(false);
  }, [layout?.image_width, layout?.image_height, workflowState]);

  useEffect(() => {
    console.log(
      "selectedCandidateId",
      selectedCandidateId === null ? "<none>" : selectedCandidateId,
    );
  }, [selectedCandidateId]);

  if (workflowState === "detecting_articles") {
    return (
      <div className="space-y-4" data-testid="image-reading-selection-loading">
        <p className="text-sm text-slate-500">
          {translate("image_reading_selection_loading")}
        </p>
      </div>
    );
  }

  if (workflowState === "error") {
    return (
      <div className="space-y-4" data-testid="image-reading-selection-error">
        <p className="text-sm text-slate-500">
          {errorMessage ||
            (errorStage === "processing_selected_article"
              ? translate("image_reading_processing_error")
              : translate("image_reading_selection_error"))}
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onRetryArticleDetection}
            className="rounded-full bg-slate-900 px-4 py-1.5 font-semibold text-white"
          >
            {errorStage === "processing_selected_article"
              ? translate("image_reading_processing_retry")
              : translate("image_reading_selection_retry")}
          </button>
          {errorStage === "processing_selected_article" ? (
            <button
              type="button"
              onClick={onChooseAnother}
              className="rounded-full border border-slate-200 px-4 py-1.5 text-slate-700"
            >
              {translate("image_reading_selection_choose_another")}
            </button>
          ) : null}
          <button
            type="button"
            onClick={onBack}
            className="rounded-full border border-slate-200 px-4 py-1.5 text-slate-700"
          >
            {translate("photo_import_mode_back")}
          </button>
        </div>
      </div>
    );
  }

  if (!imagePreviewUrl || !layout) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-slate-500">
          {translate("image_reading_selection_loading")}
        </p>
      </div>
    );
  }

  if (layout.detection_status === "none" || candidates.length === 0) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-slate-500">
          {translate("image_reading_selection_no_blocks")}
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() =>
              onConfirm([{ x: 0, y: 0, width: 1, height: 1 }])
            }
            className="rounded-full bg-slate-900 px-4 py-1.5 font-semibold text-white"
          >
            {translate("image_reading_selection_use_full_image")}
          </button>
          <button
            type="button"
            onClick={onBack}
            className="rounded-full border border-slate-200 px-4 py-1.5 text-slate-700"
          >
            {translate("photo_import_mode_back")}
          </button>
        </div>
      </div>
    );
  }

  const articleCountLabel = translate("image_reading_selection_found_count", {
    count: candidates.length,
  });

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <p className="text-sm font-semibold text-slate-500">
          {articleCountLabel}
        </p>
        <p className="text-sm text-slate-600">
          {translate("image_reading_selection_choose_text")}
        </p>
      </div>
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1.15fr)_minmax(18rem,0.85fr)] lg:items-start">
        <div className="space-y-3" data-testid="image-reading-selection-list">
          {candidates.map((candidate, index) => {
            const selected = selectedCandidateId === candidate.id;
            return (
              <button
                key={candidate.id}
                type="button"
                onClick={() => handleCandidateSelect(candidate)}
                className={`w-full rounded-[1.35rem] border px-4 py-4 text-left transition sm:px-5 ${
                  selected
                    ? "border-slate-900 bg-slate-100"
                    : "border-slate-200 bg-white hover:border-slate-300"
                }`}
              >
                <div className="flex items-start gap-3">
                  <span
                    className={`mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-sm font-semibold ${
                      selected
                        ? "border-slate-900 bg-slate-900 text-white"
                        : "border-slate-200 bg-slate-50 text-slate-600"
                    }`}
                  >
                    {index + 1}
                  </span>
                  <div className="min-w-0 space-y-1.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-base font-semibold text-slate-900">
                        {candidate.title || `Text block ${index + 1}`}
                      </p>
                      <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-600">
                        {translate("photo_import_confidence_label")}:{" "}
                        {Math.round(candidate.confidence * 100)}%
                      </span>
                    </div>
                    {candidate.preview ? (
                      <p className="line-clamp-2 text-sm leading-6 text-slate-600">
                        {candidate.preview}
                      </p>
                    ) : null}
                  </div>
                </div>
              </button>
            );
          })}
          <div className="rounded-[1.35rem] border border-slate-200 bg-slate-50 px-4 py-4 sm:px-5">
            <p className="text-sm font-medium text-slate-700">
              {translate("image_reading_selection_manual_prompt")}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setManualSelectionEnabled((current) => !current)}
                className={`rounded-full border px-4 py-1.5 text-sm font-semibold transition ${
                  manualSelectionEnabled
                    ? "border-slate-900 bg-slate-900 text-white"
                    : "border-slate-200 bg-white text-slate-700"
                }`}
              >
                {manualSelectionEnabled
                  ? translate("image_reading_selection_manual_hide")
                  : translate("image_reading_selection_manual")}
              </button>
              <button
                type="button"
                onClick={() => onConfirm([{ x: 0, y: 0, width: 1, height: 1 }])}
                className="rounded-full border border-slate-200 bg-white px-4 py-1.5 text-sm text-slate-700"
              >
                {translate("image_reading_selection_use_full_image")}
              </button>
            </div>
            {manualSelectionEnabled ? (
              <p className="mt-3 text-sm leading-6 text-slate-500">
                {translate("image_reading_selection_manual_tap_hint")}
              </p>
            ) : null}
          </div>
          {rejectedDiagnostics.length > 0 ? (
            <div className="rounded-[1.35rem] border border-slate-200 bg-white px-4 py-4 sm:px-5">
              <p className="text-sm font-medium text-slate-700">
                {translate("image_reading_candidate_diagnostics_title")}
              </p>
              <div className="mt-3 space-y-2">
                {rejectedDiagnostics.map((entry, index) => (
                  <div
                    key={`${entry.id}-${index}`}
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-semibold text-slate-900">
                        {entry.title || `Text block ${index + 1}`}
                      </p>
                      <span className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[11px] font-medium text-slate-600">
                        {translate("photo_import_confidence_label")}:{" "}
                        {Math.round(entry.confidence * 100)}%
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-slate-600">
                      {`${translate("image_reading_candidate_diagnostics_rejected")}: ${getImageReadingCandidateRejectionReasonLabel(
                        translate,
                        entry.rejection_reason,
                      )}`}
                    </p>
                    {entry.preview ? (
                      <p className="mt-1 line-clamp-2 text-sm leading-6 text-slate-500">
                        {entry.preview}
                      </p>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          ) : null}
          {rejectedDiagnostics.length > 0 ? (
            <p className="px-1 text-xs leading-5 text-slate-500">
              {translate("image_reading_candidate_diagnostics_hint")}
            </p>
          ) : null}
        </div>
        <div className="space-y-3">
          <div className="px-1 pt-1">
            <p className="text-xs font-medium uppercase tracking-[0.14em] text-slate-400">
              {translate("image_reading_selection_page_label")}
            </p>
          </div>
          <div data-testid="image-reading-selection-surface" className="pt-2 sm:pt-3">
            <div className="relative mx-auto w-full max-w-[42rem] rotate-[-0.75deg]">
              <div className="relative overflow-hidden rounded-[0.7rem] border border-stone-200 bg-white p-2.5 sm:p-3">
                <img
                  src={imagePreviewUrl}
                  alt=""
                  className="mx-auto h-auto w-full max-h-[72vh] object-contain"
                />
                {manualSelectionEnabled ? (
                  <svg
                    data-testid="image-reading-manual-overlay"
                    className="absolute inset-0 h-full w-full pointer-events-auto"
                    viewBox="0 0 100 100"
                    preserveAspectRatio="none"
                  >
                    {candidates.map((candidate, index) => {
                      const candidateBlocks = blocksForCandidate(candidate);
                      const selected = selectedCandidateId === candidate.id;
                      return (
                        <g key={`${candidate.id}-${index}`}>
                          {candidateBlocks.map((block, blockIndex) => (
                            <rect
                              key={`${candidate.id}-block-${blockIndex}`}
                              x={block.x * 100}
                              y={block.y * 100}
                              width={block.width * 100}
                              height={block.height * 100}
                              fill={
                                selected
                                  ? "rgba(15, 23, 42, 0.1)"
                                  : "rgba(255, 255, 255, 0.06)"
                              }
                              stroke={
                                selected
                                  ? "rgba(15, 23, 42, 0.32)"
                                  : "rgba(148, 163, 184, 0.16)"
                              }
                              strokeWidth={selected ? 0.5 : 0.24}
                              rx={1}
                              onClick={() => handleCandidateSelect(candidate)}
                            />
                          ))}
                        </g>
                      );
                    })}
                  </svg>
                ) : null}
              </div>
            </div>
          </div>
          <div className="rounded-[1.15rem] border border-slate-200 bg-slate-50 px-4 py-3">
            <p className="text-sm text-slate-700">
              {selectedCandidate
                ? `${translate("image_reading_selection_selected_prefix")}: ${
                    selectedCandidate.title ||
                    `Text block ${
                      candidates.findIndex(
                        (candidate) => candidate.id === selectedCandidate.id,
                      ) + 1
                    }`
                  }`
                : translate("image_reading_selection_none_selected")}
            </p>
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <button
          type="button"
          onClick={handleConfirmSelectedCandidate}
          disabled={
            !selectedCandidateId ||
            !selectedCandidate ||
            loading ||
            workflowState !== "article_confirm"
          }
          data-testid="image-reading-selection-confirm"
          className="rounded-full bg-slate-900 px-4 py-1.5 font-semibold text-white disabled:bg-slate-300"
        >
          {translate("image_reading_selection_confirm")}
        </button>
        <button
          type="button"
          onClick={onChooseAnother}
          disabled={!selectedCandidate}
          className="rounded-full border border-slate-200 px-4 py-1.5 text-slate-700 disabled:text-slate-300"
        >
          {translate("image_reading_selection_choose_another")}
        </button>
        <button
          type="button"
          onClick={onBack}
          className="rounded-full border border-slate-200 px-4 py-1.5 text-slate-700"
        >
          {translate("photo_import_mode_back")}
        </button>
      </div>
    </div>
  );
}

function PhotoImportPanel(props: {
  translate: (key: string) => string;
  state: PhotoImportState;
  entries: PhotoVocabularyImportWord[];
  selectionSet: Set<string>;
  onToggleToken: (tokenId: string) => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
  onFinalize: () => void;
  onDismiss: () => void;
  finalizeDisabled: boolean;
  isFinalizing: boolean;
  mode: PhotoVocabularyImportMode | null;
  loading: boolean;
  errorMessage: string | null;
  targetLanguages: string[];
  onToggleTargetLanguage: (lang: string) => void;
  availableLanguages: typeof TEXT_LANGUAGES;
  translationResults: {
    token_id: string;
    vocabulary_id?: number;
    translation_language: string;
    translation_word: string;
    translation_sentence: string;
  }[];
  onPreviewTranslations: () => void;
  previewLoading: boolean;
}) {
  const {
    translate,
    state,
    entries,
    selectionSet,
    onToggleToken,
    onSelectAll,
    onClearSelection,
    onFinalize,
    onDismiss,
    finalizeDisabled,
    isFinalizing,
    mode,
    loading,
    errorMessage,
    targetLanguages,
    onToggleTargetLanguage,
    availableLanguages,
    translationResults,
    onPreviewTranslations,
    previewLoading,
  } = props;

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex flex-col items-center gap-2 py-8 text-center">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-slate-200 border-t-slate-500" />
          <p className="text-base font-semibold text-slate-900">
            {translate("photo_import_processing_title")}
          </p>
          <p className="text-sm text-slate-500">
            {translate("photo_import_processing_subtitle")}
          </p>
        </div>
      );
    }
    if (state === "error") {
      return (
        <div className="flex flex-col items-center gap-3 py-6 text-center">
          <ExclamationTriangleIcon className="h-10 w-10 text-rose-500" />
          <p className="text-base font-semibold text-slate-900">
            {translate("photo_import_error_title")}
          </p>
          <p className="text-sm text-slate-500">
            {errorMessage || translate("photo_import_no_words")}
          </p>
          <button
            type="button"
            className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:border-slate-400"
            onClick={onDismiss}
          >
            {translate("photo_import_error_retry")}
          </button>
        </div>
      );
    }
    if (state === "complete") {
      return (
        <div className="flex flex-col items-center gap-3 py-6 text-center">
          <CheckCircleIcon className="h-10 w-10 text-emerald-500" />
          <p className="text-base font-semibold text-slate-900">
            {translate("photo_import_section_complete")}
          </p>
          <Link
            to="/app/vocabulary_progress"
            search={() => ({
              generatedTextId: undefined,
              tab: "photo",
            })}
            className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:border-slate-400"
            onClick={onDismiss}
          >
            {translate("vocabulary_tab_photo")}
          </Link>
          <button
            type="button"
            className="text-sm font-semibold text-slate-500"
            onClick={onDismiss}
          >
            {translate("photo_import_dismiss")}
          </button>
        </div>
      );
    }
    if (state === "review") {
      return (
        <div className="space-y-4">
          <div>
            <p className="text-sm font-semibold text-slate-900">
              {translate("photo_import_review_title")}
            </p>
            <p className="text-xs text-slate-500">
              {translate("photo_import_review_description")}
            </p>
          </div>
          <div className="space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {translate("photo_import_translation_targets") ??
                "Translation targets"}
            </p>
            <div className="flex flex-wrap gap-2">
              {availableLanguages.map((lang) => {
                const selected = targetLanguages.includes(lang.id);
                return (
                  <button
                    key={lang.id}
                    type="button"
                    onClick={() => onToggleTargetLanguage(lang.id)}
                    className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                      selected
                        ? "border-blue-500 bg-blue-50 text-blue-700"
                        : "border-slate-200 text-slate-600 hover:border-slate-400"
                    }`}
                  >
                    {translate(lang.prettyTranslationKey)}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-slate-500">
              {translate("photo_import_translation_hint") ??
                "Choose the languages you want translations in."}
            </p>
            <button
              type="button"
              onClick={onPreviewTranslations}
              disabled={previewLoading}
              className="rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {previewLoading
                ? translate("photo_import_processing_subtitle")
                : translate("photo_import_generate_translations") ??
                  "Generate translations"}
            </button>
          </div>
          {entries.length === 0 ? (
            <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
              {translate("photo_import_no_words")}
            </p>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2 text-xs font-semibold text-slate-600">
                <button
                  type="button"
                  onClick={onSelectAll}
                  className="rounded-full border border-slate-200 px-3 py-1 hover:border-slate-400"
                >
                  {translate("photo_import_select_all")}
                </button>
                <button
                  type="button"
                  onClick={onClearSelection}
                  className="rounded-full border border-slate-200 px-3 py-1 hover:border-slate-400"
                >
                  {translate("photo_import_clear_all")}
                </button>
                <span className="text-slate-400">•</span>
                <span>
                  {selectionSet.size} / {entries.length}
                </span>
              </div>
              <div className="divide-y divide-slate-100 rounded-2xl border border-slate-200">
                {entries.map((entry) => (
                  <label
                    key={entry.token_id}
                    className="flex cursor-pointer gap-3 p-3"
                  >
                    <input
                      type="checkbox"
                      checked={selectionSet.has(entry.token_id)}
                      onChange={() => onToggleToken(entry.token_id)}
                      className="mt-1 h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
                    />
                  <div className="space-y-1">
                    <p className="text-sm font-semibold text-slate-900">
                      {entry.word}
                    </p>
                    {translationResults
                      .filter((tr) => tr.token_id === entry.token_id)
                      .map((tr) => {
                        const lang =
                          availableLanguages.find(
                            (l) => l.id === tr.translation_language,
                          )?.short ?? tr.translation_language;
                        return (
                          <p
                            key={`${tr.token_id}-${tr.translation_language}-word`}
                            className="text-xs text-slate-600"
                          >
                            <span className="font-semibold text-slate-500">
                              {lang}:
                            </span>{" "}
                            {tr.translation_word}
                          </p>
                        );
                      })}
                    {entry.sentence && (
                      <p className="text-xs text-slate-500">
                        <span className="font-semibold text-slate-600">
                          {translate("photo_import_sentence_label")}:
                        </span>{" "}
                        {entry.sentence}
                      </p>
                    )}
                    {translationResults
                      .filter((tr) => tr.token_id === entry.token_id)
                      .map((tr) => {
                        const lang =
                          availableLanguages.find(
                            (l) => l.id === tr.translation_language,
                          )?.short ?? tr.translation_language;
                        if (!tr.translation_sentence) {
                          return null;
                        }
                        return (
                          <p
                            key={`${tr.token_id}-${tr.translation_language}-sentence`}
                            className="text-[11px] text-slate-500"
                          >
                            <span className="font-semibold text-slate-500">
                              {lang} — {translate("photo_import_sentence_label")}:
                            </span>{" "}
                            {tr.translation_sentence}
                          </p>
                        );
                      })}
                    <div className="flex flex-wrap gap-3 text-[11px] uppercase tracking-wide text-slate-400">
                      {entry.language && (
                        <span>
                          {translate("photo_import_language_label")}:{" "}
                          <span className="text-slate-600">
                              {entry.language}
                            </span>
                          </span>
                        )}
                        {typeof entry.confidence === "number" && (
                          <span>
                            {translate("photo_import_confidence_label")}:{" "}
                            <span className="text-slate-600">
                              {Math.round(entry.confidence * 100)}%
                            </span>
                          </span>
                        )}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 hover:border-slate-400"
                  onClick={onClearSelection}
                >
                  {translate("photo_import_deselect_all")}
                </button>
              </div>
              <button
                type="button"
                className="w-full rounded-full bg-slate-900 py-2 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                onClick={onFinalize}
                disabled={finalizeDisabled}
              >
                {isFinalizing
                  ? translate("photo_import_processing_subtitle")
                  : translate("photo_import_finalize_button")}
              </button>
            </>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="mt-6 rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-lg flex max-h-[75vh] flex-col">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-slate-900">
            {translate("photo_import_section_title")}
          </p>
          {mode && (
            <p className="text-xs text-slate-500">
              {mode === "ALL_WORDS"
                ? translate("photo_import_mode_all_words")
                : translate("photo_import_mode_highlighted_words")}
            </p>
          )}
        </div>
        <button
          type="button"
          disabled={loading}
          className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-60"
          onClick={onDismiss}
        >
          {translate("photo_import_dismiss")}
        </button>
      </div>
      <div className="mt-4 flex-1 overflow-y-auto pr-1">{renderContent()}</div>
    </div>
  );
}
